import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Shield, TrendingUp, TrendingDown, Minus, CheckCircle, Trophy,
  ChevronDown, ChevronUp, X, AlertTriangle
} from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

function RankBadge(props) {
  var change = props.change;
  if (change > 0) return <span className="flex items-center gap-0.5 text-[10px] font-bold text-emerald-600"><TrendingUp size={10} />+{change}</span>;
  if (change < 0) return <span className="flex items-center gap-0.5 text-[10px] font-bold text-red-500"><TrendingDown size={10} />{change}</span>;
  return <span className="flex items-center gap-0.5 text-[10px] text-slate-400"><Minus size={10} />Same</span>;
}

function InsightRow(props) {
  var insight = props.insight;
  var iconClass = insight.type === 'positive' ? 'text-emerald-500' : insight.type === 'warning' ? 'text-amber-500' : 'text-slate-400';
  var Icon = insight.type === 'positive' ? CheckCircle : insight.type === 'warning' ? AlertTriangle : Shield;
  return (
    <div className="flex items-start gap-2 py-1">
      <Icon size={13} className={iconClass + ' mt-0.5 flex-shrink-0'} />
      <p className="text-[11px] text-slate-700">{insight.text}</p>
    </div>
  );
}

function WeeklySummaryCard(props) {
  var summary = props.summary;
  var onDismiss = props.onDismiss;
  var navigate = useNavigate();
  var getAuthHeaders = useAuth().getAuthHeaders;
  var expanded = useState(false);
  var isExpanded = expanded[0];
  var setExpanded = expanded[1];

  if (!summary || summary.is_read) return null;

  var credScore = summary.credibility_score || 0;
  var trustScore = summary.trust_score || 50;
  var onTimeRate = summary.on_time_rate || 100;
  var streak = summary.current_streak || 0;
  var rank = summary.rank || 0;
  var rankChange = summary.rank_change || 0;
  var motivation = summary.motivation || '';
  var insights = summary.financial_insights || [];
  var nextCircle = summary.next_circle;
  var progressPct = summary.progress_to_next || 0;
  var pointsToNext = summary.points_to_next || 0;
  var circle = summary.current_circle || 'Bronze';

  function handleDismiss() {
    axios.post(API_URL + '/api/rewards/weekly-summary/mark-read', {}, { headers: getAuthHeaders() }).catch(function() {});
    if (onDismiss) onDismiss();
  }

  return (
    <Card className="glass-card border-l-4 border-l-[#0B1F3A] overflow-hidden" data-testid="weekly-summary-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-[#0B1F3A]/10">
              <Shield size={16} className="text-[#0B1F3A]" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900">Weekly Financial Summary</p>
              <p className="text-[10px] text-slate-400">Your financial progress this week</p>
            </div>
          </div>
          <button onClick={handleDismiss} className="p-1 rounded-full hover:bg-slate-100" data-testid="dismiss-summary-btn">
            <X size={14} className="text-slate-400" />
          </button>
        </div>

        {/* Trust-First Key Stats */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className="text-center p-2 rounded-lg bg-blue-50">
            <p className="text-lg font-black text-blue-700">{trustScore}</p>
            <p className="text-[9px] text-slate-500">Trust</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-emerald-50">
            <p className="text-lg font-black text-emerald-600">{onTimeRate}%</p>
            <p className="text-[9px] text-slate-500">On-time</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-[#0B1F3A]/5">
            <p className="text-lg font-black text-[#0B1F3A]">#{rank}</p>
            <p className="text-[9px] text-slate-500">Rank <RankBadge change={rankChange} /></p>
          </div>
          <div className="text-center p-2 rounded-lg bg-amber-50">
            <p className="text-lg font-black text-amber-700">{credScore}</p>
            <p className="text-[9px] text-slate-500">Credibility</p>
          </div>
        </div>

        {/* Financial Insights (always visible, up to 2) */}
        {insights.length > 0 && (
          <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 mb-3" data-testid="financial-insights">
            {insights.slice(0, 2).map(function(insight, i) {
              return <InsightRow key={i} insight={insight} />;
            })}
          </div>
        )}

        {/* Motivation */}
        <div className="p-2.5 rounded-lg bg-[#0B1F3A]/5 mb-3">
          <p className="text-xs text-slate-700 italic">&quot;{motivation}&quot;</p>
        </div>

        {/* Expandable Detail */}
        <button
          onClick={function() { setExpanded(!isExpanded); }}
          className="flex items-center gap-1 text-xs font-medium text-[#2272C5] mb-2"
          data-testid="expand-summary-btn"
        >
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {isExpanded ? 'Less detail' : 'More detail'}
        </button>

        {isExpanded && (
          <div className="space-y-3 pt-1" data-testid="summary-detail">
            {/* Circle Progress */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-600">{circle} Circle</span>
                {nextCircle && <span className="text-slate-500">{progressPct}% to {nextCircle}</span>}
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#0B1F3A] to-[#2272C5]"
                  style={{ width: Math.min(progressPct, 100) + '%' }}
                />
              </div>
              {nextCircle && <p className="text-[10px] text-slate-400 mt-1">{pointsToNext} credibility to {nextCircle}</p>}
            </div>

            {/* Remaining insights */}
            {insights.length > 2 && (
              <div className="space-y-0.5">
                {insights.slice(2).map(function(insight, i) {
                  return <InsightRow key={i} insight={insight} />;
                })}
              </div>
            )}
          </div>
        )}

        {/* CTA */}
        <Button
          size="sm"
          className="w-full mt-3 bg-[#0B1F3A] text-white text-xs rounded-xl"
          onClick={function() { navigate('/rewards'); }}
          data-testid="view-rewards-btn"
        >
          <Trophy size={13} className="mr-1.5" /> View Full Financial Profile
        </Button>
      </CardContent>
    </Card>
  );
}

export default WeeklySummaryCard;
